// Ed25519 device-key management for Bluetooth offline transfers.
//
// Private key bytes are stored in IndexedDB (not localStorage) to reduce
// the XSS exfiltration surface. @noble/ed25519 needs the raw 32-byte secret
// to sign, so a non-extractable Web Crypto CryptoKey is not usable here.
// On Capacitor (native), prefer a Keychain plugin.
import * as ed from "@noble/ed25519";
import { openDB, type IDBPDatabase } from "idb";
import { getDeviceId } from "@/lib/device";
import { supabase } from "@/integrations/supabase/client";

const LEGACY_SK_KEY = "cashisle_ble_sk";
const LEGACY_PK_KEY = "cashisle_ble_pk";

const DB_NAME = "cashisle_ble_keys";
const STORE = "keys";
const SK_ID = "sk";
const PK_ID = "pk";

let dbPromise: Promise<IDBPDatabase> | null = null;
function db() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE);
      },
    });
  }
  return dbPromise;
}

export function b64encode(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function readKey(id: string): Promise<Uint8Array | null> {
  const d = await db();
  const v = (await d.get(STORE, id)) as Uint8Array | undefined;
  return v ? new Uint8Array(v) : null;
}

async function writeKey(id: string, bytes: Uint8Array) {
  const d = await db();
  await d.put(STORE, bytes, id);
}

async function migrateFromLocalStorage(): Promise<{ sk: Uint8Array; pk: Uint8Array } | null> {
  try {
    const skB64 = localStorage.getItem(LEGACY_SK_KEY);
    const pkB64 = localStorage.getItem(LEGACY_PK_KEY);
    if (!skB64 || !pkB64) return null;
    const sk = b64decode(skB64);
    const pk = b64decode(pkB64);
    await writeKey(SK_ID, sk);
    await writeKey(PK_ID, pk);
    localStorage.removeItem(LEGACY_SK_KEY);
    localStorage.removeItem(LEGACY_PK_KEY);
    return { sk, pk };
  } catch {
    return null;
  }
}

export async function getOrCreateKeypair(): Promise<{ sk: Uint8Array; pk: Uint8Array }> {
  const sk = await readKey(SK_ID);
  const pk = await readKey(PK_ID);
  if (sk && pk) return { sk, pk };

  const migrated = await migrateFromLocalStorage();
  if (migrated) return migrated;

  const newSk = ed.utils.randomSecretKey();
  const newPk = await ed.getPublicKeyAsync(newSk);
  await writeKey(SK_ID, newSk);
  await writeKey(PK_ID, newPk);
  return { sk: newSk, pk: newPk };
}

export async function getPublicKeyB64(): Promise<string | null> {
  const pk = await readKey(PK_ID);
  if (pk) return b64encode(pk);
  // Best-effort legacy fallback
  const legacy = localStorage.getItem(LEGACY_PK_KEY);
  return legacy;
}

/** Sign canonical voucher payload. */
export async function signVoucher(
  sk: Uint8Array,
  voucher: {
    id: string;
    sender_id: string;
    receiver_id: string;
    amount_cents: number;
    currency: string;
    nonce: string;
    expires_at: string;
  },
): Promise<string> {
  const s = JSON.stringify({
    id: voucher.id,
    sender_id: voucher.sender_id,
    receiver_id: voucher.receiver_id,
    amount_cents: voucher.amount_cents,
    currency: voucher.currency,
    nonce: voucher.nonce,
    expires_at: voucher.expires_at,
  });
  const sig = await ed.signAsync(new TextEncoder().encode(s), sk);
  return b64encode(sig);
}

/** Register the current device's public key with the server (PIN required). */
export async function registerDeviceKey(pin: string, label?: string) {
  const { pk } = await getOrCreateKeypair();
  const { data, error } = await supabase.functions.invoke("register-device-key", {
    body: {
      device_id: getDeviceId(),
      public_key: b64encode(pk),
      pin,
      label,
    },
  });
  const err = (error as any)?.message || (data as any)?.error;
  if (err) throw new Error(err);
  return true;
}
